import { css } from "@emotion/react";
import {
  HighlightOffTwoTone,
  Link as LinkIcon,
  MoreVert,
  OpenInNew,
  Share,
  SvgIconComponent,
} from "@mui/icons-material";
import { Theme, useTheme } from "@mui/material";
import Link from "next/link";
import { useState } from "react";
import { match } from "ts-pattern";

import { buildResponseContent } from "~/components/database/detail/database-content/lib";
import type {
  Form,
  Response,
} from "~/components/database/detail/database-content/types";

type DialogKind =
  | "edit"
  | "share"
  | "copy-link"
  | "delete"
  | "report"
  | "cancel"
  | "open";

type MenuItem = {
  icon: SvgIconComponent;
  text: string;
  kind: DialogKind;
};

const DIALOG_MENUS: MenuItem[] = [
  { icon: OpenInNew, text: "この投稿を開く", kind: "open" },
  { icon: Share, text: "シェア", kind: "share" },
  { icon: LinkIcon, text: "リンクをコピー", kind: "copy-link" },
  { icon: HighlightOffTwoTone, text: "キャンセル", kind: "cancel" },
];

type OnClickHandler = (form: Form, response: Response) => void;

const shareContent: OnClickHandler = async (form: Form, response: Response) => {
  const title = `「${form.title}」への投稿`;
  const text = buildResponseContent(form, response);

  if (navigator.share) {
    await navigator.share({ title, text, url: window.location.href });
  }
};

const copyLink: OnClickHandler = async (form: Form, response: Response) => {
  const url = window.location.origin + `/item/${response.id}`;
  await global.navigator.clipboard.writeText(url);
  alert("URLをクリップボードにコピーしました");
};

const DialogMenuItem = ({
  form,
  item: { icon: Icon, text, kind },
  response,
  closeDialog,
}: {
  form: Form;
  item: MenuItem;
  response: Response;
  closeDialog: () => void;
}) => {
  const theme = useTheme();
  const onClick = () => {
    match(kind)
      .with("share", () => shareContent(form, response))
      .with("copy-link", () => copyLink(form, response))
      .with("report", () => void 0)
      .with("cancel", () => void 0)
      .with("delete", () => void 0)
      .with("edit", () => void 0)
      .with("open", () => void 0)
      .exhaustive();
    closeDialog();
  };

  if (kind === "open") {
    return (
      <Link
        css={dialogMenuChildrenContainer(theme)}
        href={`/item/${response.id}`}
      >
        <Icon fontSize="small" />
        <span>{text}</span>
      </Link>
    );
  }

  return (
    <div css={dialogMenuChildrenContainer(theme)} onClick={onClick}>
      <Icon fontSize="small" />
      <span>{text}</span>
    </div>
  );
};

const dialogMenuChildrenContainer = (theme: Theme) => css`
  display: flex;
  height: 2rem;
  padding: 0.625rem;
  gap: 0.5rem;
  align-items: center;

  &:hover {
    background-color: ${theme.palette.background.paper};
  }
`;

export const DialogMenu = ({
  form,
  response,
}: {
  form: Form;
  response: Response;
}) => {
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const closeDialog = () => setIsOpen(false);

  return (
    <div css={rootStyle}>
      <button
        css={dialogToggleButtonStyle(theme)}
        onClick={() => setIsOpen((prevState) => !prevState)}
      >
        <MoreVert />
      </button>

      {isOpen && (
        <>
          <div
            css={dialogButtonOverlayStyle}
            onClick={() => setIsOpen(false)}
          />
          <div
            role="button"
            onClick={() => {}}
            tabIndex={0}
            css={dialogButtonContainerStyle}
          >
            {DIALOG_MENUS.map((item, i) => (
              <div
                key={`DialogMenuChildren-${i}`}
                css={dialogButtonStyle(theme)}
              >
                <DialogMenuItem
                  item={item}
                  response={response}
                  closeDialog={closeDialog}
                  form={form}
                />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const rootStyle = css`
  position: relative;
  font-size: 0.75rem;
`;

const dialogToggleButtonStyle = (theme: Theme) => css`
  color: ${theme.palette.primary.main};
  background: none;
  border: none;
  cursor: pointer;
`;

const dialogButtonOverlayStyle = css`
  width: 100vw;
  height: 100dvh;
  position: fixed;
  top: 0;
  left: 0;
`;

const dialogButtonContainerStyle = css`
  position: absolute;
  top: 0;
  left: calc(100% + 4px);
  width: 10rem;
  background-color: #fff;
  border-radius: 0.5rem;
  filter: drop-shadow(1px 3px 5px rgba(0, 0, 0, 0.2));
`;

const dialogButtonStyle = (theme: Theme) => css`
  &:not(:first-of-type) {
    border-top: 1px solid ${theme.palette.divider};
  }
`;
